/**
 * s3Upload.js
 *
 * Direct-to-S3 upload flow with client-side thumbnail generation:
 *   Step 1 — resize to big (1280px), medium (800px), small (200px) in canvas
 *   Step 2 — ask the backend for 3 pre-signed S3 PUT URLs (one per size)
 *   Step 3 — PUT all 3 binaries directly to S3 in parallel
 *   Step 4 — confirm to the backend → it saves the DB record (no server-side resize)
 *
 * Why client-side thumbnails?
 *   The backend resize flow required the server to re-download the image from S3
 *   and run GD, adding ~3-5s per image. Doing it in canvas before upload eliminates
 *   that round-trip entirely and lets the 3 PUTs run in parallel.
 */

import { apiCall } from '../redux/api';

/**
 * Resize a File/Blob to fit within maxPx × maxPx, output as JPEG Blob.
 */
function resizeToBlob(file, maxPx, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w > maxPx || h > maxPx) {
        if (w >= h) { h = Math.round(h * maxPx / w); w = maxPx; }
        else        { w = Math.round(w * maxPx / h); h = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', quality);
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Upload a single image for a patient's health record using pre-signed S3 URLs.
 * Thumbnails are generated client-side and uploaded in parallel.
 *
 * @param {number|string} patientId
 * @param {File|Blob}     file          — the image file to upload
 * @param {string|null}   description   — optional caption saved in the DB
 * @returns {Promise<{ id: number, image: string }>}   the saved PatientImage record
 */
export async function uploadImagePresigned(patientId, file, description = null) {
  console.log('[UPLOAD] START patientId=' + patientId + ' fileName=' + file.name + ' fileSize=' + file.size);

  // ── Step 1: resize to 3 sizes client-side in parallel ──────────────────────
  console.log('[UPLOAD] Step 1 - client resize START');
  const [bigBlob, medBlob, smlBlob] = await Promise.all([
    resizeToBlob(file, 1280, 0.88),
    resizeToBlob(file, 800,  0.85),
    resizeToBlob(file, 200,  0.80),
  ]);
  console.log('[UPLOAD] Step 1 - resize DONE big=' + bigBlob.size + ' med=' + medBlob.size + ' sml=' + smlBlob.size);

  // ── Step 2: request 3 pre-signed PUT URLs from the backend ─────────────────
  console.log('[UPLOAD] Step 2 - presign START');
  let presignRes;
  try {
    presignRes = await apiCall(
      `/api/patients/${patientId}/hc/uploads/presign`,
      { mime_type: 'image/jpeg' },
      null,
      'POST'
    );
  } catch (err) {
    console.error('[UPLOAD] Step 2 - presign THREW', err);
    throw err;
  }
  console.log('[UPLOAD] Step 2 - presign DONE status=' + presignRes?.status);

  if (!presignRes || presignRes.status !== 200) {
    throw new Error(`presign_upload failed (HTTP ${presignRes?.status ?? 'no response'})`);
  }

  const responseData = presignRes.data?.data ?? presignRes.data;
  const { presigned_urls: presignedUrls, s3_key: s3Key } = responseData;

  if (!presignedUrls?.big || !presignedUrls?.medium || !presignedUrls?.small || !s3Key) {
    console.error('[UPLOAD] Step 2 - presign returned invalid data', responseData);
    throw new Error('presign_upload returned invalid data');
  }
  console.log('[UPLOAD] Step 2 - got s3Key=' + s3Key);

  // ── Step 3: PUT all 3 sizes to S3 in parallel ──────────────────────────────
  console.log('[UPLOAD] Step 3 - S3 parallel PUT START');
  const putHeaders = { 'Content-Type': 'image/jpeg' };
  const [bigRes, medRes, smlRes] = await Promise.all([
    fetch(presignedUrls.big,    { method: 'PUT', body: bigBlob, headers: putHeaders }),
    fetch(presignedUrls.medium, { method: 'PUT', body: medBlob, headers: putHeaders }),
    fetch(presignedUrls.small,  { method: 'PUT', body: smlBlob, headers: putHeaders }),
  ]);

  if (!bigRes.ok || !medRes.ok || !smlRes.ok) {
    const codes = [bigRes.status, medRes.status, smlRes.status];
    console.error('[UPLOAD] Step 3 - S3 PUT FAILED codes=' + codes.join(','));
    throw new Error(`S3 PUT failed (${codes.join('/')})`);
  }
  console.log('[UPLOAD] Step 3 - S3 PUT DONE');

  // ── Step 4: confirm to the backend → DB record saved (no server-side resize) ─
  console.log('[UPLOAD] Step 4 - confirm START s3Key=' + s3Key);
  let confirmRes;
  try {
    confirmRes = await apiCall(
      `/api/patients/${patientId}/hc/uploads/confirm`,
      {
        s3_key:      s3Key,
        description: description ?? undefined,
      },
      null,
      'POST'
    );
  } catch (err) {
    console.error('[UPLOAD] Step 4 - confirm THREW', err);
    throw err;
  }
  console.log('[UPLOAD] Step 4 - confirm DONE status=' + confirmRes?.status);

  if (!confirmRes || confirmRes.status !== 200) {
    throw new Error(`confirm_upload failed (HTTP ${confirmRes?.status ?? 'no response'})`);
  }

  console.log('[UPLOAD] DONE OK patientId=' + patientId + ' s3Key=' + s3Key);
  return confirmRes.data?.data ?? confirmRes.data;
}

/**
 * Convert a data-URI string (from react-html5-camera-photo) to a File object
 * so it can be passed to uploadImagePresigned.
 *
 * @param {string} dataUri   e.g. "data:image/jpeg;base64,/9j/4AAQ…"
 * @param {string} filename  optional filename (default "photo.jpg")
 * @returns {File}
 */
export function dataUriToFile(dataUri, filename = 'photo.jpg') {
  const [header, base64] = dataUri.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}
